/**
 * OCT20C-F1 (2026-10-22) — the app's floating controls never sit on top of an operational action.
 *
 * Field finding F1 (docs/field-test-2026-10-20/oct20a-BROWSER2.md): the fixed, draggable "Report an Issue" button
 * covered the last controls of a page ("Vehicle returned — check in", "Schedule remaining work" on the FOH Workboard;
 * "View Tech Passport" on Shop Profile) and the page could not scroll any further, so they could not be uncovered.
 * Per-page bottom padding would have to be repeated on every page (40+ pages use `h-screen` / `min-h-screen` shells)
 * and re-measured whenever a floating control changes. Instead the app shell RESERVES the floating controls' footprint:
 *
 *   1. Every floating app control carries `data-floating-control` (FLOATING_CONTROL_ATTR): Report an Issue, the
 *      tutorial Help / expand buttons, the schedule dock, the onboarding HUD, the departure-review notice, the EVE dot.
 *   2. `startFloatingControlsReserve` measures them (on mount, resize, visual-viewport change, the controls' own
 *      resizes and their mounting/unmounting) and writes the height they occupy above the viewport bottom — plus a gap —
 *      to the CSS variable `--floating-controls-reserve` on <html>.
 *   3. index.css applies that variable to the app's single route container (`main.app-floating-reserve`):
 *        - as bottom padding, so a window-scrolling page can always scroll its last control above the controls;
 *        - subtracted from `h-screen` / `min-h-screen` (and their svh/dvh forms) inside it, so a page that scrolls
 *          INSIDE a viewport-tall shell (Shop Profile, Birdseye, Team Management, …) ends above the controls and
 *          its own scroll container never runs underneath them.
 *      Dialogs and sheets are portalled to <body>, outside the container, so they are not resized; the controls that
 *      would sit above a modal (Help is z-10000) step aside while a modal holds the page (`data-yield-to-modal`).
 *
 * The Report-an-Issue button itself is confined to a bottom rail (see ReportIssueButton): it can still be dragged
 * (horizontally) and remembers where, but no remembered position can lift it into the page above the reserve.
 */

import { useEffect } from 'react';

export const FLOATING_CONTROL_ATTR = 'data-floating-control';
export const FLOATING_RESERVE_VAR = '--floating-controls-reserve';

/** Gap kept between the top of the highest floating control and the page's last control. */
export const FLOATING_RESERVE_GAP_PX = 8;
/** A control counts as bottom-anchored when its bottom edge is within this distance of the viewport bottom. */
export const FLOATING_BOTTOM_ANCHOR_PX = 160;
/** The reserve never takes more than this share of the viewport (an expanded panel must not squeeze the page away). */
export const FLOATING_RESERVE_MAX_FRACTION = 0.5;
/**
 * OCT20D-F1 — on a SHORT viewport half the height is less than the app's standard bottom controls need even when
 * nothing is expanded: at 667×375 (landscape phone) the collapsed schedule dock (88 px clearance + two wrapped panel
 * headers) measured 232 px + the 8 px gap, and the departure notice 226 px, against a 188 px cap — page controls sat
 * under them for good, with no way to uncover them. There the cap may grow up to this many px (≈ 40 px of headroom
 * over those measurements) …
 */
export const FLOATING_RESERVE_SHORT_VIEWPORT_CAP_PX = 280;
/** … as long as the page keeps at least this much of the viewport. */
export const FLOATING_RESERVE_MIN_PAGE_PX = 120;

/**
 * The most the reserve may take: half the viewport — or, on a short viewport, up to
 * FLOATING_RESERVE_SHORT_VIEWPORT_CAP_PX while the page keeps FLOATING_RESERVE_MIN_PAGE_PX. Viewports at least
 * 2 × 280 = 560 px tall keep exactly the half-viewport cap.
 */
export function floatingReserveCap(viewportHeight: number, maxFraction = FLOATING_RESERVE_MAX_FRACTION): number {
  const half = viewportHeight * maxFraction;
  const shortFloor = Math.min(FLOATING_RESERVE_SHORT_VIEWPORT_CAP_PX, viewportHeight - FLOATING_RESERVE_MIN_PAGE_PX);
  return Math.max(half, shortFloor);
}

export interface FloatingRect {
  top: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * The height (px, integer) the page must keep clear at the bottom of the viewport so that no floating control
 * overlaps it. 0 when no bottom-anchored floating control is shown.
 */
export function computeFloatingReserve(
  rects: readonly FloatingRect[],
  viewportHeight: number,
  opts: { gap?: number; anchorWithin?: number; maxFraction?: number } = {},
): number {
  const gap = opts.gap ?? FLOATING_RESERVE_GAP_PX;
  const anchorWithin = opts.anchorWithin ?? FLOATING_BOTTOM_ANCHOR_PX;
  const maxFraction = opts.maxFraction ?? FLOATING_RESERVE_MAX_FRACTION;
  if (!(viewportHeight > 0)) return 0;
  let occupied = 0;
  for (const r of rects) {
    if (!(r.width > 0) || !(r.height > 0)) continue;           // not rendered / an empty dock
    if (r.bottom < viewportHeight - anchorWithin) continue;     // not anchored to the bottom edge
    if (r.top >= viewportHeight) continue;                      // entirely below the viewport
    occupied = Math.max(occupied, viewportHeight - Math.max(0, r.top));
  }
  if (occupied <= 0) return 0;
  return Math.ceil(Math.min(occupied + gap, floatingReserveCap(viewportHeight, maxFraction)));
}

function isRendered(el: Element, win: Window): boolean {
  const cs = win.getComputedStyle(el);
  // `visibility: hidden` (a control yielding to a modal) keeps its reserve so the page behind does not reflow.
  return cs.display !== 'none';
}

/** Measure the floating controls in `doc` and return the reserve. */
export function measureFloatingReserve(doc: Document = document, win: Window = window): number {
  const rects: FloatingRect[] = [];
  doc.querySelectorAll(`[${FLOATING_CONTROL_ATTR}]`).forEach((el) => {
    if (!isRendered(el, win)) return;
    const r = el.getBoundingClientRect();
    rects.push({ top: r.top, bottom: r.bottom, width: r.width, height: r.height });
  });
  // Fixed controls and getBoundingClientRect are both relative to the layout viewport (innerHeight).
  return computeFloatingReserve(rects, win.innerHeight);
}

/**
 * Keep `--floating-controls-reserve` current. Returns a stop function. Safe to call once at app start; a second
 * call while one is running is a no-op returning the same stop function.
 */
let running: (() => void) | null = null;
export function startFloatingControlsReserve(doc: Document = document, win: Window = window): () => void {
  if (running) return running;
  const root = doc.documentElement;
  let frame = 0;
  let last = -1;
  const observed = new Set<Element>();
  const RO = (win as Window & typeof globalThis).ResizeObserver;
  const ro = typeof RO === 'function' ? new RO(() => schedule()) : null;

  const apply = () => {
    frame = 0;
    // (Re)observe the current controls so a control that grows (a dock panel expanding) updates the reserve.
    if (ro) {
      const now = new Set(doc.querySelectorAll(`[${FLOATING_CONTROL_ATTR}]`));
      now.forEach((el) => { if (!observed.has(el)) { ro.observe(el); observed.add(el); } });
      observed.forEach((el) => { if (!now.has(el)) { ro.unobserve(el); observed.delete(el); } });
    }
    const px = measureFloatingReserve(doc, win);
    if (px !== last) {
      last = px;
      root.style.setProperty(FLOATING_RESERVE_VAR, `${px}px`);
      root.setAttribute('data-floating-reserve-px', String(px));
    }
    win.dispatchEvent(new Event(FLOATING_CONTROLS_CHANGED_EVENT));
  };
  function schedule() {
    if (frame) return;
    frame = typeof win.requestAnimationFrame === 'function'
      ? win.requestAnimationFrame(apply)
      : (win.setTimeout(apply, 16) as unknown as number);
  }

  // Controls mount/unmount (childList) or gain/lose the attribute; nothing else is watched.
  const mo = new (win as Window & typeof globalThis).MutationObserver((records) => {
    for (const rec of records) {
      if (rec.type === 'attributes') { schedule(); return; }
      for (const n of [...Array.from(rec.addedNodes), ...Array.from(rec.removedNodes)] as Node[]) {
        if (n.nodeType !== 1) continue;
        const el = n as Element;
        if (el.hasAttribute(FLOATING_CONTROL_ATTR) || el.querySelector(`[${FLOATING_CONTROL_ATTR}]`) || observed.has(el)) {
          schedule();
          return;
        }
      }
      // A removed control's parent subtree (e.g. a whole provider) — cheap fallback: any observed node detached.
      if (rec.removedNodes.length) {
        for (const el of observed) { if (!el.isConnected) { schedule(); return; } }
      }
    }
  });
  mo.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: [FLOATING_CONTROL_ATTR, 'hidden'] });
  win.addEventListener('resize', schedule);
  win.addEventListener('orientationchange', schedule);
  win.visualViewport?.addEventListener('resize', schedule);
  apply();

  running = () => {
    mo.disconnect();
    ro?.disconnect();
    win.removeEventListener('resize', schedule);
    win.removeEventListener('orientationchange', schedule);
    win.visualViewport?.removeEventListener('resize', schedule);
    if (frame && typeof win.cancelAnimationFrame === 'function') win.cancelAnimationFrame(frame);
    root.style.removeProperty(FLOATING_RESERVE_VAR);
    root.removeAttribute('data-floating-reserve-px');
    running = null;
  };
  return running;
}

/* ── Report-an-Issue bottom rail ─────────────────────────────────────────────────────────────────────────────── */

/** Side margin of the rail. */
export const REPORT_RAIL_MARGIN_PX = 16;

/**
 * Clamp a remembered/dragged horizontal position onto the rail for the current viewport and button width. A position
 * remembered on a wider device, or before a rotation, lands on-screen; NaN / garbage lands at the left margin.
 */
export function clampRailX(x: unknown, viewportWidth: number, buttonWidth: number, margin = REPORT_RAIL_MARGIN_PX): number {
  const max = Math.max(margin, viewportWidth - buttonWidth - margin);
  const n = typeof x === 'number' && Number.isFinite(x) ? x : margin;
  return Math.round(Math.min(max, Math.max(margin, n)));
}

/** Read a remembered position. Accepts the legacy `{x, y}` shape (y is ignored: the rail fixes it). */
export function parseRememberedRailX(raw: string | null): number | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (v && typeof v === 'object' && typeof v.x === 'number' && Number.isFinite(v.x)) return v.x;
  } catch {
    /* corrupt value: fall back to the default */
  }
  return null;
}

/** Mounted once by the app shell (App.tsx): keeps the reserve variable current for the app's lifetime. */
export function FloatingControlsReserveHost(): null {
  useEffect(() => startFloatingControlsReserve(), []);
  return null;
}

/** Fired on window after the reserve is re-measured (a floating control mounted, unmounted, resized, …). */
export const FLOATING_CONTROLS_CHANGED_EVENT = 'optibay:floating-controls-changed';

export interface RailObstacle { left: number; right: number }

/**
 * Place the Report-an-Issue button on the rail at (or as near as possible to) `x` without overlapping another
 * floating control that shares its row (the Help button at bottom-right): a remembered or dragged position that would
 * hide it under Help moves to the nearest free side. If no free spot exists (a full-width notice), the plain clamp.
 */
export function placeOnRail(
  x: unknown, viewportWidth: number, buttonWidth: number, obstacles: readonly RailObstacle[],
  margin = REPORT_RAIL_MARGIN_PX, gap = 8,
): number {
  const want = clampRailX(x, viewportWidth, buttonWidth, margin);
  const min = margin;
  const max = Math.max(margin, viewportWidth - buttonWidth - margin);
  const free = (p: number) => obstacles.every((o) => p + buttonWidth + gap <= o.left || p >= o.right + gap);
  if (free(want)) return want;
  const candidates = obstacles
    .flatMap((o) => [Math.floor(o.left - gap - buttonWidth), Math.ceil(o.right + gap)])
    .filter((p) => p >= min && p <= max && free(p))
    .sort((a, b) => Math.abs(a - want) - Math.abs(b - want));
  return candidates.length ? candidates[0] : want;
}

/** The other floating controls sharing `el`'s row (vertical overlap), as horizontal obstacles. */
export function railObstaclesFor(el: Element, doc: Document = document): RailObstacle[] {
  const me = el.getBoundingClientRect();
  const out: RailObstacle[] = [];
  doc.querySelectorAll(`[${FLOATING_CONTROL_ATTR}]`).forEach((other) => {
    if (other === el || other.contains(el) || el.contains(other)) return;
    const r = other.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    if (r.top >= me.bottom || r.bottom <= me.top) return;
    out.push({ left: r.left, right: r.right });
  });
  return out;
}
